import React, { useEffect, useRef, useState } from 'react';
import { motion, animate, useInView } from 'framer-motion';
import { moneyShort } from './helpers';

/* ───────────────── Count-up ─────────────────
 * Anima un número de 0 → value cuando el elemento entra en viewport.
 * `format` recibe el valor intermedio (float) y devuelve el texto. */
export function CountUp({ value, format = (v) => Math.round(v).toLocaleString('es-MX'), duration = 1.1 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return undefined;
    const controls = animate(0, Number(value) || 0, {
      duration,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setDisplay(v),
    });
    return () => controls.stop();
  }, [inView, value, duration]);

  return <span ref={ref}>{format(display)}</span>;
}

/* ───────────────── Dona animada (SVG) ─────────────────
 * Cada segmento es un círculo ROTADO a su ángulo de inicio que se "dibuja"
 * (pathLength 0→frac). Con hover (onSegmentHover) o clic (onSegment) muestra
 * el drill-down. Los segmentos intermedios llevan un pelo de traslape para que
 * no queden hilos blancos entre colores. */
export function AnimatedDonut({
  rows, total, size = 160, stroke = 22, onSegment, onSegmentHover,
  /* Centro configurable (composición de portafolio pinta $ por saldo pero
   * muestra contratos al centro); defaults = comportamiento del semáforo. */
  centerValue, centerLabel = 'contratos', centerFormat = (v) => Math.round(v),
}) {
  const r = (size - stroke) / 2;
  const cx = size / 2;
  const visibles = rows.filter((x) => x.count > 0);
  const interactivo = !!(onSegment || onSegmentHover);

  let acc = 0;
  const segs = visibles.map((x) => {
    const frac = total > 0 ? x.count / total : 0;
    const seg = { ...x, frac, start: acc };
    acc += frac;
    return seg;
  });

  return (
    <div className="donut2" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={cx} cy={cx} r={r} fill="none" stroke="var(--line)" strokeWidth={stroke} opacity={0.5} />
        {segs.map((s, i) => (
          <motion.circle
            key={s.key}
            cx={cx}
            cy={cx}
            r={r}
            fill="none"
            stroke={s.color}
            strokeWidth={stroke}
            strokeLinecap="butt"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: Math.min(1, s.frac + (i < segs.length - 1 ? 0.004 : 0)) }}
            transition={{ duration: 0.9, delay: 0.15 + s.start * 0.7, ease: [0.16, 1, 0.3, 1] }}
            transform={`rotate(${-90 + s.start * 360} ${cx} ${cx})`}
            whileHover={interactivo ? { strokeWidth: stroke + 5 } : undefined}
            style={{ cursor: interactivo ? 'pointer' : 'default' }}
            onClick={onSegment ? () => onSegment(s.key) : undefined}
            onMouseEnter={onSegmentHover ? () => onSegmentHover(s.key) : undefined}
          />
        ))}
      </svg>
      <div className="donut2-center">
        <b><CountUp value={centerValue ?? total} format={centerFormat} /></b>
        <span>{centerLabel}</span>
      </div>
    </div>
  );
}

/* ───────────────── Barras verticales (crecen de abajo) ───────────────── */
export function VBars({ data, color = 'var(--pei-cyan)', highlightIndex = -1, format = moneyShort }) {
  const max = Math.max(1, ...data.map((d) => Number(d.monto) || 0));
  return (
    <div className="vbars">
      {data.map((d, i) => {
        const h = ((Number(d.monto) || 0) / max) * 100;
        const isHi = i === highlightIndex;
        return (
          <div className="vbar-col" key={d.mes || i} title={format(d.monto)}>
            <span className="vbar-val">{format(d.monto)}</span>
            <div className="vbar-track">
              <motion.div
                className="vbar-fill"
                style={{ background: isHi ? 'var(--et-corriente)' : color }}
                initial={{ height: 0 }}
                animate={{ height: `${h}%` }}
                transition={{ duration: 0.8, delay: 0.1 + i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              />
            </div>
            <span className={'vbar-lbl' + (isHi ? ' hi' : '')}>{d.label}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ───────────────── Scatter (ticket vs meses remanentes) ─────────────────
 * points: [{ id, clave, cliente, x (meses), y ($), }]. Color por urgencia de
 * vencimiento (<12m rojo, 12–24m ámbar, >24m cyan), radio ∝ ticket. Ejes
 * mínimos: ticks X cada 12m, 3 ticks Y con moneyShort. Tooltip nativo (title). */
export function ScatterChart({ points, height = 250, onPoint, onHover, onMove, onLeave }) {
  const W = 460;
  const H = 260;
  const PAD = { l: 58, r: 14, t: 12, b: 40 };
  const maxX = Math.max(12, ...points.map((p) => Number(p.x) || 0)) + 2;
  const maxY = Math.max(1, ...points.map((p) => Number(p.y) || 0)) * 1.08;
  const px = (v) => PAD.l + (v / maxX) * (W - PAD.l - PAD.r);
  const py = (v) => H - PAD.b - (v / maxY) * (H - PAD.t - PAD.b);
  const colorDe = (m) => (m < 12 ? '#DC2626' : m < 24 ? '#F0961E' : '#34C6F1');
  const rDe = (y) => 4 + (Math.sqrt((Number(y) || 0) / maxY)) * 9;
  const xTicks = [];
  for (let t = 0; t <= maxX; t += 12) xTicks.push(t);
  const yTicks = [maxY * 0.25, maxY * 0.55, maxY * 0.85];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height }} role="img">
      {yTicks.map((t) => (
        <g key={t}>
          <line x1={PAD.l} x2={W - PAD.r} y1={py(t)} y2={py(t)} stroke="var(--line-soft)" />
          <text x={PAD.l - 6} y={py(t) + 3} textAnchor="end" fontSize="9" fill="var(--ink-3)">{moneyShort(t)}</text>
        </g>
      ))}
      {xTicks.map((t) => (
        <text key={t} x={px(t)} y={H - PAD.b + 16} textAnchor="middle" fontSize="9" fill="var(--ink-3)">{t}m</text>
      ))}
      <line x1={PAD.l} x2={W - PAD.r} y1={H - PAD.b} y2={H - PAD.b} stroke="var(--line)" />
      <text x={16} y={(H - PAD.b) / 2 + PAD.t} textAnchor="middle" transform={`rotate(-90 16 ${(H - PAD.b) / 2 + PAD.t})`} fontSize="10" fontWeight="600" fill="var(--ink-2)">Valor factura c/IVA</text>
      <text x={PAD.l + (W - PAD.l - PAD.r) / 2} y={H - 6} textAnchor="middle" fontSize="10" fontWeight="600" fill="var(--ink-2)">Meses al vencimiento</text>
      {points.map((p, i) => (
        <motion.circle
          key={p.id || i}
          cx={px(Number(p.x) || 0)}
          cy={py(Number(p.y) || 0)}
          fill={colorDe(Number(p.x) || 0)}
          fillOpacity={0.62}
          stroke={colorDe(Number(p.x) || 0)}
          strokeWidth={1}
          initial={{ r: 0 }}
          animate={{ r: rDe(p.y) }}
          transition={{ duration: 0.5, delay: 0.1 + i * 0.02, ease: [0.16, 1, 0.3, 1] }}
          style={{ cursor: onPoint ? 'pointer' : 'default' }}
          onClick={onPoint ? () => onPoint(p) : undefined}
          onMouseEnter={onHover ? (e) => onHover(p, e) : undefined}
          onMouseMove={onMove ? (e) => onMove(e) : undefined}
          onMouseLeave={onLeave ? () => onLeave() : undefined}
        >
          {!onHover ? <title>{`${p.clave} · ${p.cliente || ''} — ${moneyShort(p.y)} · ${p.x} meses restantes`}</title> : null}
        </motion.circle>
      ))}
    </svg>
  );
}

/* ───────────────── Gráfica multi-serie (evolución 12m / AF amortización) ─────
 * data:   [{ label, simulado?, ...valores }]
 * series: [{ key, label, color }] — el orden define el apilado (abajo→arriba).
 * mode:   'stacked' (apilada) | 'grouped' (barras lado a lado) | 'line' (líneas).
 * Los meses con `simulado` llevan la etiqueta punteada + tooltip (reconstrucción
 * teórica vs snapshot real). Sin librerías: divs animados y SVG plano. */
export function MultiSeriesChart({ data, series, mode = 'stacked', valueTop = true, valueFormat = moneyShort }) {
  const totalDe = (d) => series.reduce((s, x) => s + (Number(d[x.key]) || 0), 0);
  const maxTotal = Math.max(1, ...data.map(totalDe));
  const maxSolo = Math.max(1, ...data.map((d) => Math.max(...series.map((x) => Number(d[x.key]) || 0))));
  const simTitle = 'Reconstrucción teórica del calendario (simulado) — aún sin snapshot real de este mes';

  if (mode === 'line') {
    const W = 100;
    const H = 100;
    const px = (i) => (data.length > 1 ? (i / (data.length - 1)) * W : W / 2);
    const py = (v) => H - ((Number(v) || 0) / maxTotal) * (H - 6);
    return (
      <div>
        <div className="ms-lines">
          <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
            {series.map((s) => (
              <motion.polyline
                key={s.key}
                points={data.map((d, i) => `${px(i)},${py(d[s.key])}`).join(' ')}
                fill="none"
                stroke={s.color}
                strokeWidth={2}
                vectorEffect="non-scaling-stroke"
                strokeLinejoin="round"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              />
            ))}
          </svg>
        </div>
        <div className="ms-lines-lbls">
          {data.map((d, i) => (
            <span className={'ms-lbl' + (d.simulado ? ' sim' : '')} key={d.mes || i}
              title={d.simulado ? simTitle : undefined}>{d.label}</span>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="ms-chart">
      {data.map((d, i) => {
        const tooltip = series.map((s) => `${s.label}: ${valueFormat(d[s.key])}`).join(' · ')
          + (d.simulado ? ' · SIMULADO' : '');
        return (
          <div className="ms-col" key={d.mes || i} title={tooltip}>
            {valueTop ? <span className="ms-val">{valueFormat(totalDe(d))}</span> : null}
            {mode === 'grouped' ? (
              <div className="ms-track grouped">
                {series.map((s) => (
                  <div className="ms-gbar" key={s.key}>
                    <motion.div
                      style={{ background: s.color, borderRadius: '4px 4px 0 0' }}
                      initial={{ height: 0 }}
                      animate={{ height: `${((Number(d[s.key]) || 0) / maxSolo) * 100}%` }}
                      transition={{ duration: 0.7, delay: 0.08 + i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="ms-track" style={d.simulado ? { opacity: 0.82 } : undefined}>
                {series.map((s) => (
                  <motion.div
                    key={s.key}
                    style={{ background: s.color }}
                    initial={{ height: 0 }}
                    animate={{ height: `${((Number(d[s.key]) || 0) / maxTotal) * 100}%` }}
                    transition={{ duration: 0.7, delay: 0.08 + i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                  />
                ))}
              </div>
            )}
            <span className={'ms-lbl' + (d.simulado ? ' sim' : '')}
              title={d.simulado ? simTitle : undefined}>{d.label}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ───────────────── Barras horizontales (crecen de izquierda) ───────────────── */
export function HBars({ data, valueKey = 'saldo', onRow, onRowHover, onLeave, format = moneyShort }) {
  const max = Math.max(1, ...data.map((d) => Number(d[valueKey]) || 0));
  return (
    <div className="hbars" onMouseLeave={onLeave}>
      {data.map((d, i) => {
        const w = ((Number(d[valueKey]) || 0) / max) * 100;
        return (
          <button
            type="button"
            className={'hbar-row' + (onRow || onRowHover ? ' clickable' : '')}
            key={d.key || d.etapa || i}
            onClick={onRow ? (e) => onRow(d, e) : undefined}
            onMouseEnter={onRowHover ? (e) => onRowHover(d, e) : undefined}
          >
            <span className="hbar-lbl">
              <span className="sw" style={{ background: d.color }} />
              {d.label}
              <span className="hbar-ct">{d.count}</span>
            </span>
            <div className="hbar-track">
              <motion.div
                className="hbar-fill"
                style={{ background: d.color }}
                initial={{ width: 0 }}
                animate={{ width: `${w}%` }}
                transition={{ duration: 0.8, delay: 0.1 + i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              />
            </div>
            <span className="hbar-val">{format(d[valueKey])}</span>
          </button>
        );
      })}
    </div>
  );
}
